import {PasswordChangeAction} from './PasswordChangeActions'
import {
    PasswordChangeInput,
    PasswordChangeState,
    PasswordChangeStatus,
    PasswordChangeValidation
} from './PasswordChangeState'
import * as Result from '../../Data/Result'
import {decodeHttpResponse, HttpError} from '../../Data/Http'
import {update} from '../../Data/ObjectHelper'
import {
    PasswordChangeFeedback,
    PasswordChangeRequestIdentifier
} from './PasswordChangeFeedback'
import {
    equalsValidator,
    mandatoryStringValidator,
    validateInputField
} from '../../Data/FormValidation'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import * as PasswordChangeResponse from '../../Entities/PasswordChangeResponse'
import * as PasswordChangeData from '../../Entities/PasswordChangeData'
import * as PasswordChangeResource from '../../Request/PasswordChangeResource'

type PasswordChangeLoop =
    FeedbackLoop<PasswordChangeState, PasswordChangeFeedback>

export function init(): PasswordChangeState  {
    return {
        input: {
            currentPassword: '',
            newPassword: '',
            newPasswordRepetition: '',
        },
        validation: {
            currentPassword: null,
            newPassword: null,
            newPasswordRepetition: null,
        },
        submissionAttempted: false,
        status: {type: 'Pending'},
    }
}

export function reduce(
    state: PasswordChangeState,
    action: PasswordChangeAction
): PasswordChangeLoop {
    switch (action.type) {
        case 'OldPasswordChanged':
            return {
                state: update({
                    input: update({
                        currentPassword: action.value,
                    }, state.input),
                    validation: update({
                        currentPassword:
                            validateInputField(
                                validators.currentPassword,
                                state.submissionAttempted,
                                action.value),
                    }, state.validation),
                    status:
                        state.status.type === 'PasswordWrong' ?
                            {type: 'Pending'} :
                            state.status
                }, state),
                feedback: [],
            }
        case 'NewPasswordChanged':
            return {
                state: update({
                    input: update({
                        newPassword: action.value,
                    }, state.input),
                    validation: update({
                        newPassword:
                            validateInputField(
                                validators.newPassword,
                                state.submissionAttempted,
                                action.value),
                        newPasswordRepetition:
                            validateInputField(
                                validators.newPasswordRepetition(action.value),
                                state.submissionAttempted,
                                action.value),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        case 'ConfirmationPasswordChanged':
            return {
                state: update({
                    input: update({
                        newPasswordRepetition: action.value,
                    }, state.input),
                    validation: update({
                        newPasswordRepetition:
                            validateInputField(
                                validators.newPasswordRepetition(
                                    state.input.newPassword),
                                state.submissionAttempted,
                                action.value),
                    }, state.validation),
                }, state),
                feedback: [],
            }
        case 'Submit':
            const validationOutcome = validate(state.input)
            return ((): PasswordChangeLoop => {
                switch (validationOutcome.type) {
                    case 'Ok':
                        return {
                            state: update({
                                status: {type: 'Loading'},
                                submissionAttempted: false,
                            }, state),
                            feedback: [{
                                type: 'SendRequest',
                                identifier: {
                                    type: 'PasswordChangeCommand',
                                },
                                httpRequest:
                                    PasswordChangeResource.submit(
                                        validationOutcome.value,
                                    ),
                            }],
                        }
                    case 'Err':
                        return {
                            state: update({
                                submissionAttempted: true,
                                validation: validationOutcome.error,
                            }, state),
                            feedback: [],
                        }
                }
            })()
        case 'PasswordChangeCommandResponse':
            const
                result =
                    decodeHttpResponse(
                        PasswordChangeResponse.decoder(),
                        action.result),
                status =
                    ((): PasswordChangeStatus => {
                        switch (result.type) {
                            case 'Ok':
                                return result.value
                            case 'Err':
                                return {
                                    type: 'GeneralError',
                                    error: result.error,
                                }
                        }
                    })()
            return {
                state: update({status}, state),
                feedback: [],
            }
        case 'MenuButtonClicked': {
            return {
                state,
                feedback: [{type: 'OpenDrawer'}],
            }
        }
    }
}

const validators = {
    currentPassword: mandatoryStringValidator(),
    newPassword: mandatoryStringValidator(),
    newPasswordRepetition:
        (reference: string) =>
            (value: string) =>
                Result.andThen(
                    equalsValidator(reference),
                    mandatoryStringValidator()(value))
}

export function validate(
    input: PasswordChangeInput,
): Result.Result<PasswordChangeValidation, PasswordChangeData.M> {
    const validationResults = {
        currentPassword: validators.currentPassword(input.currentPassword),
        newPassword: validators.newPassword(input.newPassword),
        newPasswordRepetition:
            validators
                .newPasswordRepetition
                (input.newPassword)
                (input.newPasswordRepetition),
    }
    if (validationResults.currentPassword.type === 'Ok' &&
        validationResults.newPassword.type === 'Ok' &&
        validationResults.newPasswordRepetition.type === 'Ok'
    ) {
        return {
            type: 'Ok',
            value: {
                currentPassword: validationResults.currentPassword.value,
                newPassword: validationResults.newPassword.value,
            },
        }
    } else {
        return {
            type: 'Err',
            error: {
                currentPassword:
                    Result.errorToNullable(
                        validationResults.currentPassword),
                newPassword:
                    Result.errorToNullable(
                        validationResults.newPassword),
                newPasswordRepetition:
                    Result.errorToNullable(
                        validationResults.newPasswordRepetition),
            }
        }
    }
}

export function httpResponseAction(
    identifier: PasswordChangeRequestIdentifier,
    result: Result.Result<HttpError, string>,
): PasswordChangeAction {
    switch (identifier.type) {
        case 'PasswordChangeCommand':
            return {
                type: 'PasswordChangeCommandResponse',
                result,
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/PasswordChange/PasswordChangeReducer.ts